import { Select } from "antd";
import Card from "components/card";
import LineChart from "components/charts/LineChart";
import Loader from "components/loader";
import useStats from "hooks/useStats";
import { useEffect, useMemo, useState } from "react";
import { MdBarChart, MdOutlineCalendarToday } from "react-icons/md";

const TotalSpent = () => {
  const { isLoading: isLoadingStats, fetchData } = useStats("usuarios-creados");

  const [mensual, setMensual] = useState([]);
  const [diario, setDiario] = useState([]);
  const [dataYAxisPostulantes, setDataYAxisPostulantes] = useState([]);
  const [dataYAxisInstituciones, setDataYAxisInstituciones] = useState([]);
  const [diasMesSeleccionado, setDiasMesSeleccionado] = useState([]);
  const [totalUsuariosPorMes, setTotalUsuariosPorMes] = useState([]);
  const [selectedRange, setSelectedRange] = useState(undefined);

  const sortedRanges = useMemo(() => {
    return [...mensual].sort((a, b) => {
      if (a?.month_key && b?.month_key) {
        return new Date(a.month_key) - new Date(b.month_key);
      }
      const [monthA, yearA] = (a?.fecha || "").split("/").map(Number);
      const [monthB, yearB] = (b?.fecha || "").split("/").map(Number);
      return new Date(yearA, monthA - 1) - new Date(yearB, monthB - 1);
    });
  }, [mensual]);

  const totalUsuarios = useMemo(() => {
    return mensual.reduce((acc, item) => acc + Number(item?.total_usuarios || 0), 0);
  }, [mensual]);

  const lineChartDataTotalSpent = [
    {
      name: "Total usuarios",
      data: totalUsuariosPorMes,
      color: "#ff572f",
    },
    {
      name: "Postulantes",
      data: dataYAxisPostulantes,
      color: "#FFD625",
    },
    {
      name: "Instituciones",
      data: dataYAxisInstituciones,
      color: "#000000",
    },
  ];

  const lineChartOptionsTotalSpent = {
    legend: {
      show: true,
    },
    theme: {
      mode: "light",
    },
    chart: {
      type: "line",
      toolbar: {
        show: true,
      },
      zoom: {
        enabled: true,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
    },
    tooltip: {
      style: {
        fontSize: "12px",
        fontFamily: undefined,
        backgroundColor: "#000000",
      },
      theme: "dark",
      x: {
        format: "dd/MM/yy HH:mm",
      },
    },
    grid: {
      show: false,
    },
    xaxis: {
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: "#A3AED0",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
      type: "text",
      range: undefined,
      categories: diasMesSeleccionado,
    },
  };

  const loadStats = async () => {
    const res = await fetchData();
    const monthly = res?.mensual || [];
    const daily = res?.diario || [];
    // console.log(monthly)
    setMensual(monthly);
    setDiario(daily);
  };

  const handleChange = (range) => {
    if (!range) return;

    setSelectedRange(range);

    const [month, year] = range.split("/");
    const monthNumber = Number(month);
    const yearNumber = Number(year);

    const daysInMonth = new Date(yearNumber, monthNumber, 0).getDate();
    const todosLosDias = Array.from({ length: daysInMonth }, (_, i) =>
      String(i + 1).padStart(2, "0")
    );

    const postulantesByDay = {};
    const institucionesByDay = {};
    const totalByDay = {};

    diario
      .filter((item) => item?.mes === range)
      .forEach((item) => {
        const day = String(item?.dia || 0).padStart(2, "0");
        postulantesByDay[day] = Number(item?.postulantes || 0);
        institucionesByDay[day] = Number(item?.cuentas || 0);
        totalByDay[day] = Number(item?.total || 0);
      });

    const dataPostulantes = todosLosDias.map((dia) => postulantesByDay[dia] || 0);
    const dataInstituciones = todosLosDias.map((dia) => institucionesByDay[dia] || 0);
    const dataTotal = todosLosDias.map((dia) => totalByDay[dia] || 0);

    setDiasMesSeleccionado(todosLosDias);
    setDataYAxisPostulantes(dataPostulantes);
    setDataYAxisInstituciones(dataInstituciones);
    setTotalUsuariosPorMes(dataTotal);
  };

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    if (!sortedRanges.length) return;
    const latestRange = sortedRanges[sortedRanges.length - 1]?.fecha;
    if (!latestRange) return;
    handleChange(latestRange);
  }, [sortedRanges, diario]);

  return (
    <Card extra="!p-[20px] text-center h-[500px]">
      <div className="flex justify-between">
        <button className="linear mt-1 flex items-center justify-center gap-2 rounded-lg bg-lightPrimary p-2 text-gray-600 transition duration-200 hover:cursor-pointer hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:hover:opacity-90 dark:active:opacity-80">
          <MdOutlineCalendarToday />

          <Select
            value={selectedRange}
            style={{ width: 120 }}
            onChange={handleChange}
            options={sortedRanges?.filter(s => s.fecha != null && s.fecha != '').map((item) => ({
              value: item.fecha,
              label: item.fecha,
            }))}
          />
        </button>

        <div className="flex items-center gap-3">
          <p className=" text-sm text-gray-600">Total usuarios</p>
          <p className="text-3xl font-bold text-navy-700 dark:text-white">
            {totalUsuarios.toLocaleString("es-AR")}
          </p>
        </div>

        <button className="!linear z-[1] flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-brand-500 !transition !duration-200 hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10">
          <MdBarChart className="h-6 w-6" />
        </button>
      </div>

      <div className="flex h-full w-full flex-row justify-between sm:flex-wrap lg:flex-nowrap 2xl:overflow-hidden">
        <div className="h-full w-full">
          {isLoadingStats ? (
            <Loader />
          ) : (
            <LineChart options={lineChartOptionsTotalSpent} series={lineChartDataTotalSpent} />
          )}
        </div>
      </div>
    </Card>
  );
};

export default TotalSpent;
